import { useState, useEffect, useCallback } from "react";
import { useHubAuth } from "@/context/HubAuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays, FileText, TrendingUp, Wrench,
  Plus, Trash2, LogOut, Loader2, X, Check
} from "lucide-react";

const TABS = [
  { id: "tasks", label: "Tasks & Calendar", icon: CalendarDays },
  { id: "plan", label: "Business Plan", icon: FileText },
  { id: "progress", label: "Progress", icon: TrendingUp },
  { id: "adjustments", label: "Site Adjustments", icon: Wrench },
];

const PRIORITY_COLORS = {
  high: "bg-red-500/15 text-red-400 border-red-500/20",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
};

const STATUS_COLORS = {
  todo: "bg-white/10 text-white/60",
  in_progress: "bg-blue-500/15 text-blue-400",
  done: "bg-emerald-500/15 text-emerald-400",
  pending: "bg-amber-500/15 text-amber-400",
  completed: "bg-emerald-500/15 text-emerald-400",
};

export default function HubDashboard() {
  const { hubUser, hubLogout } = useHubAuth();
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [plan, setPlan] = useState({ content: "", last_edited_by: "", updated_at: "" });
  const [progress, setProgress] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddProgress, setShowAddProgress] = useState(false);
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [t, p, pr, a] = await Promise.all([
        api.get("/hub/tasks"), api.get("/hub/business-plan"),
        api.get("/hub/progress"), api.get("/hub/adjustments"),
      ]);
      setTasks(t.data);
      setPlan(p.data);
      setProgress(pr.data);
      setAdjustments(a.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ===== Task handlers =====
  const addTask = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post("/hub/tasks", {
        title: fd.get("title"), description: fd.get("description"),
        priority: fd.get("priority"), due_date: fd.get("due_date"), status: "todo",
      });
      setTasks([data, ...tasks]);
      setShowAddTask(false);
      toast.success("Task added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateTaskStatus = async (id, status) => {
    await api.patch(`/hub/tasks/${id}`, { status });
    setTasks(tasks.map(t => t.task_id === id ? { ...t, status } : t));
  };

  const deleteTask = async (id) => {
    await api.delete(`/hub/tasks/${id}`);
    setTasks(tasks.filter(t => t.task_id !== id));
  };

  // ===== Business Plan =====
  const savePlan = async () => {
    setSaving(true);
    try {
      await api.put("/hub/business-plan", { content: plan.content });
      toast.success("Business plan saved");
      const { data } = await api.get("/hub/business-plan");
      setPlan(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // ===== Progress =====
  const addProgress = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post("/hub/progress", { title: fd.get("title"), description: fd.get("description"), status: fd.get("status") });
      setProgress([data, ...progress]);
      setShowAddProgress(false);
      toast.success("Progress item added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteProgress = async (id) => {
    await api.delete(`/hub/progress/${id}`);
    setProgress(progress.filter(p => p.progress_id !== id));
  };

  // ===== Adjustments =====
  const addAdjustment = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post("/hub/adjustments", { title: fd.get("title"), description: fd.get("description"), priority: fd.get("priority"), status: "pending" });
      setAdjustments([data, ...adjustments]);
      setShowAddAdjustment(false);
      toast.success("Adjustment added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateAdjustmentStatus = async (id, status) => {
    await api.patch(`/hub/adjustments/${id}`, { status });
    setAdjustments(adjustments.map(a => a.adjustment_id === id ? { ...a, status } : a));
  };

  const deleteAdjustment = async (id) => {
    await api.delete(`/hub/adjustments/${id}`);
    setAdjustments(adjustments.filter(a => a.adjustment_id !== id));
  };

  if (loading) return <div className="min-h-screen bg-[#0f1117] flex items-center justify-center"><Loader2 className="w-8 h-8 text-white/30 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0f1117] text-white" data-testid="hub-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/8 bg-[#0f1117] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-dark-256.png" alt="PawLinx" className="h-9 w-auto" data-testid="pawlinx-logo" />
            <span className="font-manrope text-xs text-white/40">Hub</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-manrope text-sm text-white/50">{hubUser?.display_name}</span>
            <button onClick={hubLogout} className="w-8 h-8 rounded-lg hover:bg-white/8 flex items-center justify-center transition-colors" data-testid="hub-logout"><LogOut className="w-4 h-4 text-white/40" strokeWidth={1.5} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-8 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-manrope text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`} data-testid={`hub-tab-${t.id}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {/* ===== TASKS TAB ===== */}
        {tab === "tasks" && (
          <div data-testid="hub-tasks-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-semibold text-xl">Tasks & Calendar</h2>
              <button onClick={() => setShowAddTask(true)} className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold flex items-center gap-1.5 transition-colors" data-testid="hub-add-task-btn"><Plus className="w-4 h-4" /> Add Task</button>
            </div>

            {showAddTask && (
              <form onSubmit={addTask} className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6 space-y-3" data-testid="hub-add-task-form">
                <input name="title" required placeholder="Task title" className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25" data-testid="hub-task-title" />
                <textarea name="description" placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25 resize-none" data-testid="hub-task-desc" />
                <div className="grid grid-cols-2 gap-3">
                  <select name="priority" defaultValue="medium" className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope" data-testid="hub-task-priority">
                    <option value="high" className="bg-[#1a1d27]">High Priority</option>
                    <option value="medium" className="bg-[#1a1d27]">Medium Priority</option>
                    <option value="low" className="bg-[#1a1d27]">Low Priority</option>
                  </select>
                  <input name="due_date" type="date" className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope" data-testid="hub-task-date" />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold transition-colors" data-testid="hub-task-submit">Add</button>
                  <button type="button" onClick={() => setShowAddTask(false)} className="border border-white/10 text-white/50 rounded-lg px-4 py-2 text-sm font-manrope transition-colors hover:bg-white/5">Cancel</button>
                </div>
              </form>
            )}

            {/* Kanban columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["todo", "in_progress", "done"].map(col => (
                <div key={col} className="bg-white/[0.03] border border-white/8 rounded-xl p-4">
                  <p className="font-manrope text-xs font-semibold uppercase tracking-widest text-white/30 mb-4">
                    {col === "todo" ? "To Do" : col === "in_progress" ? "In Progress" : "Done"} ({tasks.filter(t => t.status === col).length})
                  </p>
                  <div className="space-y-2">
                    {tasks.filter(t => t.status === col).map(task => (
                      <div key={task.task_id} className="bg-white/5 border border-white/8 rounded-xl p-4 group" data-testid={`hub-task-${task.task_id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-manrope text-sm font-medium text-white/90 leading-snug">{task.title}</p>
                          <button onClick={() => deleteTask(task.task_id)} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"><Trash2 className="w-3.5 h-3.5 text-white/30 hover:text-red-400" /></button>
                        </div>
                        {task.description && <p className="font-manrope text-xs text-white/40 mb-3 leading-relaxed">{task.description}</p>}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span>
                          {task.due_date && <span className="text-[10px] text-white/30 font-manrope">{task.due_date}</span>}
                          <span className="text-[10px] text-white/20 font-manrope ml-auto">{task.created_by}</span>
                        </div>
                        <div className="flex gap-1 mt-3 pt-3 border-t border-white/5">
                          {["todo", "in_progress", "done"].filter(s => s !== col).map(s => (
                            <button key={s} onClick={() => updateTaskStatus(task.task_id, s)} className="text-[10px] font-manrope font-medium text-white/30 hover:text-white/70 bg-white/5 hover:bg-white/10 rounded-md px-2 py-1 transition-colors">
                              {s === "todo" ? "To Do" : s === "in_progress" ? "In Progress" : "Done"}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== BUSINESS PLAN TAB ===== */}
        {tab === "plan" && (
          <div data-testid="hub-plan-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-outfit font-semibold text-xl">Business Plan</h2>
                {plan.last_edited_by && <p className="font-manrope text-xs text-white/30 mt-1">Last edited by {plan.last_edited_by} {plan.updated_at ? `on ${new Date(plan.updated_at).toLocaleDateString()}` : ""}</p>}
              </div>
              <button onClick={savePlan} disabled={saving} className="bg-[#E07A5F] hover:bg-[#C86347] disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold flex items-center gap-1.5 transition-colors" data-testid="hub-save-plan">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
            </div>
            <textarea
              value={plan.content}
              onChange={(e) => setPlan({ ...plan, content: e.target.value })}
              placeholder="Start writing your business plan here... Both users can edit and save this document."
              className="w-full min-h-[60vh] p-6 rounded-xl bg-white/5 border border-white/10 text-white/90 text-sm font-manrope leading-relaxed focus:outline-none focus:border-[#E07A5F] placeholder-white/20 resize-y"
              data-testid="hub-plan-textarea"
            />
          </div>
        )}

        {/* ===== PROGRESS TAB ===== */}
        {tab === "progress" && (
          <div data-testid="hub-progress-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-semibold text-xl">Progress Tracking</h2>
              <button onClick={() => setShowAddProgress(true)} className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold flex items-center gap-1.5 transition-colors" data-testid="hub-add-progress-btn"><Plus className="w-4 h-4" /> Add Entry</button>
            </div>
            {showAddProgress && (
              <form onSubmit={addProgress} className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6 space-y-3" data-testid="hub-add-progress-form">
                <input name="title" required placeholder="Progress title" className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25" data-testid="hub-progress-title" />
                <textarea name="description" placeholder="Details" rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25 resize-none" data-testid="hub-progress-desc" />
                <select name="status" defaultValue="in_progress" className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope" data-testid="hub-progress-status">
                  <option value="in_progress" className="bg-[#1a1d27]">In Progress</option>
                  <option value="done" className="bg-[#1a1d27]">Completed</option>
                </select>
                <div className="flex gap-2">
                  <button type="submit" className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold transition-colors">Add</button>
                  <button type="button" onClick={() => setShowAddProgress(false)} className="border border-white/10 text-white/50 rounded-lg px-4 py-2 text-sm font-manrope hover:bg-white/5">Cancel</button>
                </div>
              </form>
            )}
            <div className="space-y-3">
              {progress.map(p => (
                <div key={p.progress_id} className="bg-white/5 border border-white/8 rounded-xl p-5 flex items-start justify-between group" data-testid={`hub-progress-${p.progress_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-manrope text-sm font-medium text-white/90">{p.title}</p>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[p.status]}`}>{p.status === "in_progress" ? "In Progress" : "Done"}</span>
                    </div>
                    {p.description && <p className="font-manrope text-xs text-white/40 leading-relaxed">{p.description}</p>}
                    <p className="font-manrope text-[10px] text-white/20 mt-2">By {p.created_by} - {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => deleteProgress(p.progress_id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-3"><Trash2 className="w-4 h-4 text-white/30 hover:text-red-400" /></button>
                </div>
              ))}
              {progress.length === 0 && <p className="font-manrope text-sm text-white/20 text-center py-12">No progress entries yet. Click "Add Entry" to start tracking.</p>}
            </div>
          </div>
        )}

        {/* ===== ADJUSTMENTS TAB ===== */}
        {tab === "adjustments" && (
          <div data-testid="hub-adjustments-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-semibold text-xl">Website Adjustments</h2>
              <button onClick={() => setShowAddAdjustment(true)} className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold flex items-center gap-1.5 transition-colors" data-testid="hub-add-adjustment-btn"><Plus className="w-4 h-4" /> Add Adjustment</button>
            </div>
            {showAddAdjustment && (
              <form onSubmit={addAdjustment} className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6 space-y-3" data-testid="hub-add-adjustment-form">
                <input name="title" required placeholder="What needs to change?" className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25" data-testid="hub-adjustment-title" />
                <textarea name="description" placeholder="Details about the adjustment" rows={2} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope focus:outline-none focus:border-[#E07A5F] placeholder-white/25 resize-none" data-testid="hub-adjustment-desc" />
                <select name="priority" defaultValue="medium" className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-manrope" data-testid="hub-adjustment-priority">
                  <option value="high" className="bg-[#1a1d27]">High Priority</option>
                  <option value="medium" className="bg-[#1a1d27]">Medium Priority</option>
                  <option value="low" className="bg-[#1a1d27]">Low Priority</option>
                </select>
                <div className="flex gap-2">
                  <button type="submit" className="bg-[#E07A5F] hover:bg-[#C86347] text-white rounded-lg px-4 py-2 text-sm font-manrope font-semibold transition-colors">Add</button>
                  <button type="button" onClick={() => setShowAddAdjustment(false)} className="border border-white/10 text-white/50 rounded-lg px-4 py-2 text-sm font-manrope hover:bg-white/5">Cancel</button>
                </div>
              </form>
            )}
            <div className="space-y-3">
              {adjustments.map(a => (
                <div key={a.adjustment_id} className="bg-white/5 border border-white/8 rounded-xl p-5 group" data-testid={`hub-adj-${a.adjustment_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-manrope text-sm font-medium text-white/90">{a.title}</p>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[a.priority]}`}>{a.priority}</span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                      </div>
                      {a.description && <p className="font-manrope text-xs text-white/40 leading-relaxed mt-1">{a.description}</p>}
                      <p className="font-manrope text-[10px] text-white/20 mt-2">By {a.created_by} - {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {a.status !== "completed" && <button onClick={() => updateAdjustmentStatus(a.adjustment_id, "completed")} className="w-7 h-7 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-emerald-400" /></button>}
                      <button onClick={() => deleteAdjustment(a.adjustment_id)} className="w-7 h-7 rounded-md hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-white/30 hover:text-red-400" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {adjustments.length === 0 && <p className="font-manrope text-sm text-white/20 text-center py-12">No adjustments logged yet. Click "Add Adjustment" to track website changes.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
